import StatisticsPage, { drilldownPath } from '../../components/statistics/StatisticsPage';
import StatTile from '../../components/statistics/StatTile';
import { LineChart } from '../../components/statistics/charts';
import { useStatisticsData, useStatisticsFilters } from '../../components/statistics/useStatistics';
import { getOverview } from '../../services/statisticsService';
import { formatCount, formatDecimal, formatMinutes, formatShare } from '../../utils/period';

type Metric = { current: number | null; previous: number | null };

/** PRD §5.1 — is the marketplace growing, stable or stalling? */
const Overview = () => {
  const [filters, setFilters] = useStatisticsFilters();
  const { data, loading, error, reload } = useStatisticsData(getOverview, filters);
  const m: Record<string, Metric> = data?.metrics || {};
  const cur = (key: string) => m[key]?.current ?? null;
  const pair = (key: string) => ({ current: cur(key), previous: m[key]?.previous ?? null });
  const link = (metric: string) => drilldownPath(metric, filters);

  const activeNote = data?.posthogConfigured
    ? 'Opened the app, per PostHog. Leaves out web-only members and anyone who declined analytics.'
    : 'PostHog not configured on this server.';

  return (
    <StatisticsPage
      title="Overview"
      subtitle="How the platform did over the period, against the period before. Click a figure for the rows behind it."
      filters={filters}
      onFiltersChange={setFilters}
      period={data?.period}
      loading={loading}
      error={error}
      onRetry={reload}
    >
      <div className="stats-tiles">
        <StatTile
          label="New members"
          {...pair('newMembers')}
          format={formatCount}
          detail={`${formatShare(cur('verifiedShare'))} verified their academic email`}
          to={link('signups')}
        />
        <StatTile label="Active members" {...pair('activeMembers')} format={formatCount} note={activeNote} />
        <StatTile
          label="Rides offered"
          {...pair('ridesOffered')}
          format={formatCount}
          detail={`${formatCount(cur('seatsOffered'))} seats`}
          note="Counted by departure date."
          to={link('rides')}
        />
        <StatTile
          label="Seats filled"
          {...pair('seatsFilled')}
          format={formatCount}
          detail={`${formatShare(cur('fillRate'))} of seats offered`}
          to={link('rides')}
        />
        <StatTile
          label="Seat requests"
          {...pair('seatRequests')}
          format={formatCount}
          detail={`${formatShare(cur('requestsApproved'))} approved · ${formatShare(cur('requestsDeclined'))} declined · ${formatShare(cur('requestsUnanswered'))} unanswered`}
          note={`${formatShare(cur('requestsWithdrawn'))} withdrawn before an answer.`}
          to={link('requests')}
        />
        <StatTile
          label="Driver response time"
          {...pair('responseMedianMinutes')}
          format={formatMinutes}
          good="down"
          detail="Median, request to answer"
          to={link('requests')}
        />
        <StatTile
          label="Rides cancelled by drivers"
          {...pair('ridesCancelledByDrivers')}
          format={formatCount}
          good="down"
          detail={`Plus ${formatCount(cur('passengersRemovedByDrivers'))} passengers removed from rides that went ahead`}
          to={link('cancellations')}
        />
        <StatTile
          label="Seats cancelled by riders"
          {...pair('bookingsCancelledByRiders')}
          format={formatCount}
          good="down"
          note={
            (cur('cancellationsUnattributed') || 0) > 0
              ? `${formatCount(cur('cancellationsUnattributed'))} more from before we recorded who cancelled.`
              : undefined
          }
          to={link('cancellations')}
        />
        <StatTile
          label="Ride Captures created"
          {...pair('capturesCreated')}
          format={formatCount}
          detail={`${formatShare(cur('capturesAlertedShare'))} alerted · ${formatShare(cur('capturesSeatShare'))} ended in a seat`}
          to={link('captures')}
        />
        <StatTile
          label="Ratings"
          {...pair('ratings')}
          format={formatCount}
          detail={`Average ${formatDecimal(cur('averageRating'))} / 5`}
          to={link('ratings')}
        />
      </div>

      <section className="stats-section">
        <h2>Day by day</h2>
        <p className="stats-section-intro">Sign-ups and seat requests by the day they happened; rides by departure day.</p>
        <LineChart
          data={data?.series || []}
          series={[
            { key: 'signups', label: 'Sign-ups' },
            { key: 'requests', label: 'Seat requests' },
            { key: 'rides', label: 'Rides' },
          ]}
        />
      </section>
    </StatisticsPage>
  );
};

export default Overview;
